import { SBFormItemAddon } from '@components/SBFormItemAddon'
import { useLocalizableInput } from '@hooks/useLocalizableInput'
import { Input as ANTDInput, Form, FormItemProps, Select, Space } from 'antd'
import { Rule } from 'antd/es/form'
import { NamePath } from 'antd/es/form/interface'
import { ReactNode, useEffect, useState } from 'react'
import { langList, langsByCode } from '../../factories/Utils'
import { AvailableLocale, Lang } from '../../models/Lang'
import FormItemErrors from '../FormItemErrors'

export type LocalizableInputProps = FormItemProps & {
  selectedLangCode?: AvailableLocale
  rules?: Rule[]
  name: NamePath
  label?: ReactNode
  noStyle?: boolean
  max?: number
  /**
   * Form items name is composed of an optional prefix, `namePrefix` props, and name
   * Only `name` part will be localized
   * `namePrefix` allows to add a fixed prefix to the Form.Item names
   */
  namePrefix?: string[]
  hideLangName?: boolean
  placeholder?: string
  hideLangSelector?: boolean
}

export const Input = ({
  selectedLangCode: selectedLangProps,
  name,
  namePrefix,
  label,
  rules = [],
  hidden: hiddenProps,
  max,
  hideLangName = false,
  placeholder,
  initialValue,
  hideLangSelector = false,
}: LocalizableInputProps) => {
  const {
    selectedLangCode,
    setSelectedLangCode,
    formItemName,
    formInstance,
    showLangSelector,
    showLangFlag,
  } = useLocalizableInput({
    selectedLangProps,
    name,
    namePrefix,
    hideLangSelector,
  })

  const selectedValue = Form.useWatch(
    formItemName(selectedLangCode),
    formInstance
  )

  // Buffer the input value locally instead of binding directly to the form state.
  // Binding `value` to the Form.useWatch result causes a cursor-jump on every keystroke
  // because the controlled value round-trips through the form before returning.
  const [localValue, setLocalValue] = useState<string>('')

  useEffect(() => {
    setLocalValue(String(selectedValue ?? ''))
  }, [selectedValue])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value)
    formInstance.setFieldValue(formItemName(selectedLangCode), e.target.value)
  }

  return (
    <SBFormItemAddon
      key={selectedLangCode}
      formItemProps={{
        hidden: hiddenProps,
        name: formItemName(selectedLangCode),
        label: label,
        rules: rules,
        help: false,
        required: true,
      }}
      noWrapAddon
      addonBefore={
        // A pinned field always states its language: that label is not a
        // selector, it just says which locale the field edits.
        selectedLangProps !== undefined ? (
          <Space.Addon>{`${langsByCode[selectedLangProps].flag} ${!hideLangName ? langsByCode[selectedLangProps].name : ''}`}</Space.Addon>
        ) : showLangSelector ? (
          <Select
            defaultValue={selectedLangCode}
            style={{ width: 120 }}
            onChange={(val) => setSelectedLangCode(val)}
            options={langList.map((lang: Lang) => ({
              value: lang.code,
              label: <>{`${lang.flag} ${!hideLangName ? lang.name : ''}`}</>,
            }))}
          />
        ) : showLangFlag ? (
          // The header selector drives the language: show a passive flag so the
          // field still states which locale it edits, without its own selector.
          <Space.Addon>{langsByCode[selectedLangCode].flag}</Space.Addon>
        ) : undefined
      }
    >
      <ANTDInput
        placeholder={placeholder}
        maxLength={max}
        showCount={max !== undefined}
        value={localValue}
        onChange={handleChange}
        style={{ flex: 1 }}
      />

      {/* Champs cachés pour chaque langue */}
      {langList.map((lang) => (
        <Form.Item
          hidden
          key={lang.id}
          name={formItemName(lang.code)}
          className="m-0"
          rules={rules}
          initialValue={initialValue}
        >
          <ANTDInput type="hidden" />
        </Form.Item>
      ))}

      <FormItemErrors namePrefix={namePrefix} name={name} />
    </SBFormItemAddon>
  )
}

export type InputType = { Input: typeof Input }
